import React, { useCallback, useEffect, useState } from "react";
import { CMS_SECTIONS } from "../cms/cmsSections";
import { defaultJsonString } from "../cms/defaults";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

export default function AdminCms() {
  const [sectionKey, setSectionKey] = useState(CMS_SECTIONS[0].key);
  const [editor, setEditor] = useState("");
  const [label, setLabel] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadFromServer = useCallback(async (key) => {
    if (!isSupabaseConfigured || !supabase) {
      setEditor(defaultJsonString(key));
      setLabel(CMS_SECTIONS.find((s) => s.key === key)?.label || key);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("cms_site_content")
      .select("data, label")
      .eq("section_key", key)
      .maybeSingle();
    setLoading(false);
    if (error) {
      setMessage({ type: "danger", text: error.message });
      setEditor(defaultJsonString(key));
      return;
    }
    if (data?.data != null) {
      setEditor(JSON.stringify(data.data, null, 2));
      setLabel(data.label || "");
    } else {
      setEditor(defaultJsonString(key));
      setLabel(CMS_SECTIONS.find((s) => s.key === key)?.label || key);
    }
    setMessage(null);
  }, []);

  useEffect(() => {
    loadFromServer(sectionKey);
  }, [sectionKey, loadFromServer]);

  const fillDefaults = () => {
    setEditor(defaultJsonString(sectionKey));
    setMessage({ type: "info", text: "Editor filled with built-in defaults." });
  };

  const save = async () => {
    setMessage(null);
    let parsed;
    try {
      parsed = JSON.parse(editor);
    } catch (e) {
      setMessage({
        type: "danger",
        text: `Invalid JSON: ${e instanceof Error ? e.message : "parse error"}`,
      });
      return;
    }

    const meta = CMS_SECTIONS.find((s) => s.key === sectionKey);
    const rowLabel =
      label.trim() || meta?.label || sectionKey.replace(/_/g, " ");

    if (!isSupabaseConfigured || !supabase) {
      setMessage({
        type: "warning",
        text: "Supabase not configured — nothing was saved.",
      });
      return;
    }

    setLoading(true);
    const { error } = await supabase.from("cms_site_content").upsert(
      {
        section_key: sectionKey,
        label: rowLabel,
        data: parsed,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "section_key" }
    );
    setLoading(false);
    if (error) {
      setMessage({ type: "danger", text: error.message });
      return;
    }
    setMessage({ type: "success", text: "Saved. Public pages will use this on next load." });
  };

  const meta = CMS_SECTIONS.find((s) => s.key === sectionKey);

  if (!isSupabaseConfigured) {
    return (
      <p className="text-danger">Supabase is not configured. Add keys to .env</p>
    );
  }

  return (
    <div>
      <h1 className="h3 mb-3">CMS</h1>
      <p className="text-muted small">
        Edit JSON for each section. The live site reads these values when a row
        exists; otherwise it uses the template defaults from the repo.
      </p>

      <div className="row g-3">
        <div className="col-lg-3">
          <label className="form-label small text-muted">Section</label>
          <select
            className="form-select form-select-sm"
            value={sectionKey}
            onChange={(e) => setSectionKey(e.target.value)}
          >
            {CMS_SECTIONS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          {meta?.hint && (
            <p className="small text-muted mt-2 mb-0">{meta.hint}</p>
          )}
        </div>
        <div className="col-lg-9">
          <label className="form-label small text-muted">Label (optional)</label>
          <input
            type="text"
            className="form-control form-control-sm mb-2"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Display title in admin only"
          />
          <label className="form-label small text-muted">Payload (JSON)</label>
          <textarea
            className="form-control font-monospace small"
            rows={18}
            value={editor}
            onChange={(e) => setEditor(e.target.value)}
            spellCheck={false}
          />
          <div className="d-flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              className="btn btn_theme btn_sm"
              onClick={save}
              disabled={loading}
            >
              Save to Supabase
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={fillDefaults}
              disabled={loading}
            >
              Load template defaults
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => loadFromServer(sectionKey)}
              disabled={loading}
            >
              Reload from server
            </button>
          </div>
          {message && (
            <div className={`alert alert-${message.type} small mt-3 mb-0`}>
              {message.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
